<template>
  <AppLayout>
    <div class="-m-4 min-h-full p-4 text-surface-0">
      <div class="mb-4 mt-4 flex items-center justify-between">
        <SelectButton
          v-model="selectedMode"
          :options="modeOptions"
          optionLabel="label"
          optionValue="value"
        />
      </div>

      <DataTable :value="last3MonthsUsage" class="mb-6 mt-6">
        <Column header="Month">
          <template #body="{ data }">
            {{ dayjs(data.month).format('MMM YYYY') }}
          </template>
        </Column>
        <Column header="Services">
          <template #body="{ data }">
            <div class="flex flex-col gap-1">
              <div v-for="item in getServicesList(data.totals)" :key="item.key" class="flex justify-between gap-8">
                <span>{{ item.label }}</span>
                <span class="tabular-nums">{{ item.value }}</span>
              </div>
              <div class="mt-1 flex justify-between gap-8 border-t border-surface-500 pt-1 font-semibold">
                <span>Total</span>
                <span class="tabular-nums">{{ getServicesTotal(data.totals) }}</span>
              </div>
            </div>
          </template>
        </Column>
      </DataTable>

      <h2 class="mb-6 mt-15">Last 31 days</h2>

      <Chart type="bar" :data="chartData" :options="chartOptions" class="h-[30rem]" />
    </div>
  </AppLayout>
</template>

<script setup lang="ts">
import { ref, watch, onMounted } from 'vue';
import Chart from 'primevue/chart';
import SelectButton from 'primevue/selectbutton';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import AppLayout from '@/components/AppLayout.vue';
import dayjs from 'dayjs';
import { usePromise } from '@/composables/usePromise';
import { useApiClient } from '@/api/apiClient';
import * as Sentry from '@sentry/vue';
import type { DayUsageDto, MonthUsageDto } from '@/api/generated';
import type { ChartOptions } from 'chart.js';

const { api } = useApiClient();

// Options for the mode selector
const modeOptions = [
  { label: '# of operations', value: 'quantity' },
  { label: 'Tokens used', value: 'credits' },
];

// Selected mode: 'credits' or 'quantity'
const selectedMode = ref<'credits' | 'quantity'>('quantity');

// Service labels
const serviceLabels: Record<string, string> = {
  'export': 'Export',
  'processing': 'Processing',
  'processing:product': 'Product',
  'processing:fashion': 'Fashion',
  'processing:virtualModel': 'Virtual Model',
  'processing:virtualScene': 'Virtual Scene',
  'processing:book': 'Book',
};

// Colors for each service
const serviceColors: Record<string, string> = {
  'export': '#fb923c', // orange-400
  'processing': '#94a3b8', // slate-400
  'processing:product': '#60a5fa', // blue-400
  'processing:fashion': '#34d399', // emerald-400
  'processing:virtualModel': '#a78bfa', // violet-400
  'processing:virtualScene': '#f472b6', // pink-400
  'processing:book': '#facc15', // yellow-400
};

interface ChartDataset {
  label: string;
  backgroundColor: string;
  data: number[];
}

const {
  pending: _pending,
  error,
  run,
} = usePromise(async () => {
  const last3MonthsUsageRes = await api.usageControllerGetLast3MonthsUsage({
    query: { type: selectedMode.value },
  });

  last3MonthsUsage.value = last3MonthsUsageRes.data ?? [];

  const res = await api.usageControllerGetLast31DaysUsage({
    query: { type: selectedMode.value },
  });

  chartData.value = generateChartData(res.data ?? []);
  chartOptions.value = generateChartOptions();
});

watch(error, () => {
  if (error.value) {
    console.error(error.value);
    Sentry.captureException(error.value);
  }
});

// Load data on mount
onMounted(() => {
  run();
});

// Reload data when mode changes
watch(selectedMode, () => {
  run();
});

const last3MonthsUsage = ref<MonthUsageDto[]>([]);

function getServicesList(totals: MonthUsageDto['totals']) {
  return totals
    .filter(t => t.value > 0)
    .map((t, i) => ({
      key: `${t.service}-${i}`,
      label: t.serviceDescription || serviceLabels[t.service] || t.service,
      value: t.value,
    }));
}

function getServicesTotal(totals: MonthUsageDto['totals']) {
  return totals.reduce((sum, t) => sum + t.value, 0);
}

// Derive chart data from API response
const chartData = ref<{
  labels: string[];
  datasets: ChartDataset[];
}>({ labels: [], datasets: [] });

function generateChartData(usageData: DayUsageDto[]) {
  if (!usageData) {
    return { labels: [], datasets: [] };
  }

  // Generate date labels from the data
  const labels = usageData.map(day => dayjs(day.date).format('MMM D'));

  // Collect all unique service codes from the data
  const allServiceCodes = new Set<string>();
  usageData.forEach(day => {
    Object.keys(day.totals).forEach(serviceCode => {
      allServiceCodes.add(serviceCode);
    });
  });

  // Create a dataset for each service
  const datasets: ChartDataset[] = [];
  allServiceCodes.forEach(serviceCode => {
    const serviceData = usageData.map(day => day.totals[serviceCode] || 0);

    datasets.push({
      label: serviceLabels[serviceCode] || serviceCode,
      backgroundColor: serviceColors[serviceCode] || '#94a3b8',
      data: serviceData,
    });
  });

  return { labels, datasets };
}

const chartOptions = ref<ChartOptions<'bar'>>(generateChartOptions());

const chartTextColor = 'rgba(255, 255, 255, 0.87)';
const chartGridColor = 'rgba(255, 255, 255, 0.12)';

function generateChartOptions(): ChartOptions<'bar'> {
  return {
    maintainAspectRatio: false,
    responsive: true,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          usePointStyle: true,
          padding: 15,
          color: chartTextColor,
        },
      },
      tooltip: {
        filter: (tooltipItem: { parsed: { y: number | null } }) => {
          return tooltipItem.parsed.y !== 0;
        },
        callbacks: {
          footer: (tooltipItems: Array<{ parsed: { y: number | null } }>) => {
            const total = tooltipItems.reduce((sum: number, item) => sum + (item.parsed.y ?? 0), 0);
            const unit = selectedMode.value === 'credits' ? 'tokens' : 'operations';
            return `Total: ${total} ${unit}`;
          },
        },
      },
    },
    scales: {
      x: {
        stacked: true,
        grid: {
          display: false,
        },
        ticks: {
          color: chartTextColor,
        },
      },
      y: {
        stacked: true,
        beginAtZero: true,
        title: {
          display: true,
          text: selectedMode.value === 'credits' ? 'Tokens Used' : 'Operations',
          color: chartTextColor,
        },
        grid: {
          color: chartGridColor,
        },
        ticks: {
          color: chartTextColor,
        },
      },
    },
  };
}
</script>
